import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategoriesSection() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDropdownChange = (categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId);
    if (cat) {
      setSelectedCategory(categoryId);
      setDropdownOpen(false);
      window.open(cat.url, '_blank', 'noopener,nofollow');
    }
  };

  return (
    <section className="section-padding bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
            {t('categories.title')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
            {t('categories.subtitle')}
          </p>
        </div>

        {/* Dropdown */}
        <div className="max-w-md mx-auto mb-10 md:mb-14">
          <label className="block text-sm font-medium text-foreground-700 mb-2">
            {t('categories.dropdownLabel')}
          </label>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-800 hover:border-primary-400 transition-colors text-left"
            >
              <span className={selectedCategory ? 'text-foreground-900' : 'text-foreground-400'}>
                {selectedCategory
                  ? t(`categories.${categories.find(c => c.id === selectedCategory)?.nameKey || 'shoes'}`)
                  : t('categories.dropdownPlaceholder')}
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 max-h-72 overflow-y-auto rounded-lg bg-background-50 border border-background-200 shadow-lg z-20">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleDropdownChange(cat.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    <span className="w-6 h-6 flex items-center justify-center text-primary-500">
                      <i className={cat.icon}></i>
                    </span>
                    {t(`categories.${cat.nameKey}`)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category Buttons Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col overflow-hidden rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-200"
            >
              <div className="relative w-full h-24 md:h-28 overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(`categories.${cat.nameKey}`)}
                  title={t(`categories.${cat.nameKey}`)}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex items-center justify-center gap-2 px-3 py-3">
                <span className="w-6 h-6 flex items-center justify-center text-primary-600 shrink-0">
                  <i className={cat.icon}></i>
                </span>
                <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-primary-700 text-center leading-tight">
                  {t(`categories.${cat.nameKey}`)}
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* CTA to main site */}
        <div className="mt-10 md:mt-14 text-center">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary text-base"
          >
            {t('categories.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}